/**
 * Route copy for `src/routes/platforms` (keys: `routes.platforms.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  platformsPage: {
    thePlatform: 'The platform',
    profile: 'Profile',
    companyPage: 'Company page',
    page: 'Page',
    instagramBusinessAccount: 'Instagram Business account',
    account: 'Account',
    active: 'Active',
    expired: 'Expired',
    disconnected: 'Disconnected',
    pending: 'Pending',
    vConnectedSuccessfully: '{{v}} connected successfully.',
    connectingToThePlatformFailed: 'Connecting to the platform failed.',
    vRefreshed: '{{v}} refreshed.',
    newcountNewPageSDetectedAnd: '{{newCount}} new page(s) detected and added{{v5}}.',
    vUpToDateNoNew: '{{v}} up to date — no new page ({{pushed}} target(s) checked).',
    noPageToSyncForV: 'No page to sync for {{v}}.',
    vIsNotConnectedConnectThe: '{{v}} is not connected — connect the platform before refreshing its pages.',
    vSessionExpiredReconnectThisAccount: '{{v}} session expired. Reconnect this account to refresh the pages.',
    disconnectVPlatformUsername: 'Disconnect {{v}} ({{platform_username}})?',
    vDisconnected: '{{v}} disconnected.',
    disableThePublishingTarget: 'Disable the publishing target',
    hideAccountNameKindFromThe: 'Hide “{{account_name}}” ({{kind}}) from the publishing targets? It will no longer be offered in the composer and will not reappear during a page detection. You can re-enable it here at any time.',
    disable: 'Disable',
    accountNameDisabledHiddenFromThe: '“{{account_name}}” disabled — hidden from the composer (can be re-enabled here).',
    accountNameReEnabledPublishableAgain: '“{{account_name}}” re-enabled — publishable again.',
    lengthConnectionSToReconnectToken: ' {{length}} connection(s) to reconnect (token expired).',
    newCountNewPageSDetected: '{{new_count}} new page(s) detected and added{{v5}}.{{skipSuffix}}',
    noConnectedPublishingPlatformToScan: 'No connected publishing platform to scan.',
    noNewPageDetectedAllYour: 'No new page detected — all your pages are already synced.{{skipSuffix}}',
    platforms: 'Platforms',
    connectYourSocialNetworksToPublish: 'Connect your social networks to publish and engage your communities.',
    reDiscoverAllConnectedPlatformsAnd: 'Re-discover all connected platforms and add the new pages created since',
    detecting: 'Detecting…',
    detectNewPages: '🔎 Detect new pages',
    availableNetworks: 'Available networks',
    loadingPlatforms: 'Loading platforms…',
    noActivePlatformOnThisDeployment: 'No active platform on this deployment',
    upcomingPlatformsAreListedBelowNo: 'Upcoming platforms are listed below; no action is possible until they are enabled on the server.',
    upcomingPlatformsLengthNotConnectableYet: 'Upcoming platforms ({{length}}) — not connectable yet',
    publishingTargets: 'Publishing targets',
    everyConnectedProfileAndEveryPage: 'Every connected profile and every Page / Company Page you manage can serve as a publishing destination. Use “Detect new pages” to add a recently created page, then select it in the composer. Disable the ones you don\'t want to use (can be re-enabled here at any time).',
    loadingPublishingTargets: 'Loading publishing targets…',
    noPublishingTarget: 'No publishing target',
    connectANetworkAboveYourProfile: 'Connect a network above: your profile and the Pages you manage will appear here.',
    activeConnections: 'Active connections',
    loadingConnections: 'Loading connections…',
    noConnection: 'No connection',
    connectAPlatformAboveToFind: 'Connect a platform above to find it here.',
    labelAccountLabel2Added: '{{label}} account “{{label2}}” added.',
    connected: 'Connected',
    soon: 'Soon',
    setupRequired: 'Setup required',
    connected2: 'connected',
    setupRequiredOwnerAppId: 'Setup required (owner App ID)',
    labelIsNotSupportedOnThis: '{{label}} is not supported on this deployment yet — no action possible for now.',
    comingSoon: 'Coming soon',
    theOwnerMustConfigureThisPlatform: 'The owner must configure this platform\'s App ID / secret on this deployment.',
    setupRequired2: 'Setup required',
    redirecting: 'Redirecting…',
    connectABot: 'Connect a bot',
    reconnect: 'Reconnect',
    connect: 'Connect',
    oauthConnectionNotYetAvailableFor: 'OAuth connection not yet available for this platform — no action possible for now.',
    oauthComingSoon: 'OAuth coming soon',
    managedPagesLength: 'Managed Pages ({{length}})',
    new: 'New',
    disabled: 'Disabled',
    reEnableTheTarget: 'Re-enable the target',
    reEnable: 'Re-enable',
    disableTheTarget: 'Disable the target',
    vAddedOn: '· {{v}} · Added on',
    refresh: 'Refresh',
    reSyncThePagesChannelsManaged: 'Re-sync the Pages / channels managed by this account',
    syncing: 'Syncing…',
    refreshPages: 'Refresh pages',
    disconnect: 'Disconnect',
    connectBluesky: 'Connect Bluesky',
    blueskyUsesAnAppPasswordCreated: 'Bluesky uses an app password (created in Settings → Privacy and security → App passwords) rather than an OAuth flow.',
    blueskyAccount: 'Bluesky account',
    handle: 'Handle',
    eGMyaccountBskySocial: 'E.g. myaccount.bsky.social',
    appPassword: 'App password',
    displayNameOptional: 'Display name (optional)',
    myBlueskyAccount: 'My Bluesky account',
    connectATelegramBot: 'Connect a Telegram bot',
    telegramUsesABotCreatedVia: 'Telegram uses a bot (created via @BotFather) rather than an OAuth flow. The bot must be an administrator of the target channel or group.',
    telegramBot: 'Telegram bot',
    displayName: 'Display name',
    myAnnouncementsChannel: 'My announcements channel',
    channelGroupIdChatId: 'Channel / group ID (chat_id)',
    eGMychannelOr1001234567890: 'E.g. @mychannel or -1001234567890',
    mychannel: '@mychannel',
    botToken: 'Bot token',
    n123456AbcDef: '123456:ABC-DEF…',
    connectGoogleBusiness: 'Connect Google Business',
    googleBusinessProfilePublishesALocal: 'Google Business Profile publishes a local post (What\'s new) on a location. Enter the location\'s resource name and a Google access token.',
    googleBusinessListing: 'Google Business listing',
    locationResourceName: 'Location resource name',
    eGAccounts123456Locations789012: 'E.g. accounts/123456/locations/789012',
    googleAccessToken: 'Google access token',
    myBusinessListing: 'My business listing',
    incompleteSessionUserTenantNotFound: 'Incomplete session (user/tenant not found) — sign in again.',
    connecting: 'Connecting…',
    cancel: 'Cancel',
  },
};

export default en;
